import { ReactNode } from 'react';
import ReactDOM from 'react-dom';

import { WeeeIcon } from '@/components/common/Icon';

import { twMerge } from 'tailwind-merge';
import { CheckmarkOutline } from '@weee-fe/kunlunicon';

type toastProps = {
  text: string | ReactNode | ReactNode[];
  duration?: number;
  position?: 'bottom' | 'center';
  className?: string;
  innerClassName?: string;
  textClassName?: string;
  iconType?: string;
  showIcon?: boolean;
  isTextHtml?: boolean;
  btnText?: string | ReactNode | ReactNode[];
  handleclick?: () => void;
  afterRemove?: () => void;
  showSuffixIconChecked?: boolean;
};

const ToastElement = (props: toastProps) => {
  const {
    text,
    position,
    className,
    showIcon,
    btnText,
    handleclick,
    iconType = 'iconicon-color-information',
    innerClassName,
    isTextHtml,
    showSuffixIconChecked
  } = props;

  return (
    <div
      data-testid="wid-toast"
      style={{ transform: 'translateZ(10px)' }}
      className={twMerge(
        'fixed z-[3000] left-0 right-0 text-center px-5',
        position === 'center' ? 'top-1/2' : 'bottom-24',
        className
      )}>
      <div
        data-testid="wid-toast-content"
        className={twMerge(
          'px-5 py-3 w-auto inline-flex mx-auto items-center justify-between box-border rounded-500  bg-tint-black-800 text-reserved-true-white enki-body-xs max-w-[750px]',
          showSuffixIconChecked ? 'flex' : '',
          innerClassName
        )}>
        <div data-testid="wid-toast-content-wrap" className="max-full text-left" style={{ wordBreak: 'break-word' }}>
          {showIcon && (
            <WeeeIcon data-testid="wid-toast-icon" type={iconType} className="mr-2" style={{ fontSize: 20 }} />
          )}
          {isTextHtml ? (
            <div
              data-testid="wid-toast-text"
              dangerouslySetInnerHTML={{
                __html: text as string
              }}
            />
          ) : (
            text
          )}
        </div>
        <div data-testid="btn-right" onClick={handleclick}>
          {btnText}
        </div>
        {showSuffixIconChecked && (
          <div
            data-testid="wid-toast-suffix-icon"
            className="flex items-center justify-center border border-solid border-root-chive-green-base-1 rounded-full w-5 h-5">
            <CheckmarkOutline data-testid="wid-toast-suffix-icon-checkmark" className="text-root-chive-green-base-1" />
          </div>
        )}
      </div>
    </div>
  );
};

const Toast = ({
  text,
  duration = 3000,
  position = 'bottom',
  className,
  iconType,
  showIcon = false,
  btnText,
  afterRemove,
  handleclick,
  innerClassName,
  isTextHtml = false,
  showSuffixIconChecked = false
}: toastProps) => {
  let toastTimer = null;
  if (toastTimer) clearTimeout(toastTimer);

  removeToast();

  let div = document.createElement('div');
  div.id = 'showToast';
  document.body.appendChild(div);
  ReactDOM.render(
    <ToastElement
      text={text}
      position={position}
      showIcon={showIcon}
      className={className}
      innerClassName={innerClassName}
      iconType={iconType}
      btnText={btnText}
      isTextHtml={isTextHtml}
      handleclick={handleclick}
      showSuffixIconChecked={showSuffixIconChecked}
    />,
    div
  );

  toastTimer = setTimeout(() => {
    if (div) {
      ReactDOM.unmountComponentAtNode(div);
      div.parentNode?.removeChild(div);
      afterRemove && afterRemove();
    }
  }, duration);
};

const showToast = (text: string | ReactNode | ReactNode[], duration?: number) => {
  let toastTimer = null;
  if (toastTimer) clearTimeout(toastTimer);

  let div = document.getElementById('showToast');
  if (!div) {
    div = document.createElement('div');
    div.id = 'showToast';
    document.body.appendChild(div);
    ReactDOM.render(<ToastElement text={text} />, div);
  }

  toastTimer = setTimeout(() => {
    if (div) {
      ReactDOM.unmountComponentAtNode(div);
      div.parentNode?.removeChild(div);
    }
  }, duration || 3000);
};

const removeToast = () => {
  let div = document.getElementById('showToast');
  if (div) {
    ReactDOM.unmountComponentAtNode(div);
    div.parentNode?.removeChild(div);
  }
};

Toast.success = (content, duration?, onClose?, mask?) => {
  Toast({
    text: content,
    duration,
    afterRemove: onClose
  });
};

Toast.info = (content, duration?, onClose?, mask?, position?) => {
  Toast({
    text: content,
    duration,
    afterRemove: onClose,
    position
  });
};

export default Toast;

export { removeToast, showToast };
